// src/contexts/AuthContext.js
import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Issuer } from 'src/utils/auth';
import { login, getBearerToken, logout } from 'src/services/authServices';

var ActionType;
(function (ActionType) {
  ActionType['INITIALIZE'] = 'INITIALIZE';
  ActionType['SIGN_IN'] = 'SIGN_IN';
  ActionType['SIGN_OUT'] = 'SIGN_OUT';
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => ({
    ...state,
    isAuthenticated: action.payload.isAuthenticated,
    isInitialized: true,
    user: action.payload.user
  }),

  SIGN_IN: (state, action) => ({
    ...state,
    isAuthenticated: true,
    user: action.payload.user
  }),

  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  })
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // INITIALIZATION (check if token exists)
  const initialize = useCallback(async () => {
    const token = getBearerToken();

    if (token) {
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: true,
          user: null // Optional: load /me if needed
        }
      });
    } else {
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // SIGN IN
  const signIn = useCallback(async (username, password) => {
    const token = await login(username, password);

    dispatch({
      type: ActionType.SIGN_IN,
      payload: { user: null }
    });

    return token;
  }, []);

  // SIGN OUT
  const signOut = useCallback(() => {
    logout(); // removes localStorage token

    dispatch({
      type: ActionType.SIGN_OUT
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
